import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Popover,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { apiClient } from '../services/apiClient';
import { useCart } from '../services/CartContext';

export default function Deals() {
  const { addToCart, setIsCartOpen } = useCart();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isFirst, setIsFirst] = useState(true);
  const [isLast, setIsLast] = useState(true);
  const [error, setError] = useState(null);

  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");

  const [toastMessage, setToastMessage] = useState("");
  const [toastOpen, setToastOpen] = useState(false);
  const [hoveredImage, setHoveredImage] = useState(null);

  const getEquivalentCoupon = (deal) => {
    let typeId = deal.couponTypeId;
    let tier = deal.tier || deal.couponCode || deal.couponTypeName || 'Starter';
    const normTier = String(tier).toUpperCase();

    if (!typeId) {
      if (normTier.includes('BRONZE')) typeId = 2;
      else if (normTier.includes('SILVER')) typeId = 3;
      else if (normTier.includes('GOLD')) typeId = 4;
      else typeId = 1;
    }

    const map = {
      1: { id: 1, name: 'Starter Digital Card', price: 10, tier: 'Starter', bg: 'linear-gradient(135deg, #A8E6CF 0%, #3D8B7A 80%)' },
      2: { id: 2, name: 'Bronze Digital Card', price: 100, tier: 'Bronze', bg: 'linear-gradient(135deg, #D69A6C 0%, #A9673A 80%)' },
      3: { id: 3, name: 'Silver Digital Card', price: 500, tier: 'Silver', bg: 'linear-gradient(135deg, #D8DEDF 0%, #8F9A9C 80%)' },
      4: { id: 4, name: 'Gold Digital Card', price: 1000, tier: 'Gold', bg: 'linear-gradient(135deg, #F0D18B 0%, #B9852E 80%)' },
    };

    return map[typeId] || map[1];
  };

  const fetchProducts = async (pageNumber) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/product-item/active?page=${pageNumber}&size=6`, { skipAuth: true });
      if (response && response.success && response.data) {
        setProducts(response.data.content || []);
        setTotalPages(response.data.totalPages || 1);
        setIsFirst(response.data.first);
        setIsLast(response.data.last);
        setPage(response.data.number);
      } else {
        throw new Error(response?.message || 'Failed to fetch prize products');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong while fetching coupons.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(page);
  }, [page]);

  useEffect(() => {
    async function loadCategories() {
      try {
        const response = await apiClient.get('/product-categories/active', { skipAuth: true });
        if (response && Array.isArray(response)) {
          setCategories(response.filter(c => c.isActive));
        }
      } catch (err) {
        console.error("Failed to load active product categories", err);
      }
    }
    loadCategories();
  }, []);

  const handlePrevPage = () => {
    if (!isFirst) {
      setPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (!isLast) {
      setPage((prev) => prev + 1);
    }
  };

  const getCouponPrice = (code) => {
    if (!code) return 10;
    const c = String(code).toUpperCase();
    if (c === 'STARTER') return 10;
    if (c === 'BRONZE') return 100;
    if (c === 'SILVER') return 500;
    if (c === 'GOLD') return 1000;
    return 10; // Fallback
  };

  const handleCopyCode = (code) => {
    navigator.clipboard?.writeText(code).catch(() => { });
    setToastMessage(`Code ${code} copied`);
    setToastOpen(true);
  };

  return (
    <Box
      component="section"
      id="deals"
      sx={{
        padding: '80px 0',
        background: 'transparent',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            marginBottom: '48px',
            flexWrap: 'wrap',
            gap: '24px',
          }}
        >
          <Box sx={{ textAlign: 'left', maxWidth: '640px' }}>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '13px',
                color: '#E8C078',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
              }}
            >
              Live right now
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                fontSize: { xs: '32px', md: '40px' },
                color: '#F5F1E6',
                marginTop: '14px',
              }}
            >
              Today's assured deals
            </Typography>
            <Typography
              sx={{
                color: 'rgba(245, 241, 230, 0.66)',
                fontSize: '16px',
                marginTop: '14px',
              }}
            >
              Every deal below is unlocked using a <strong>₹10</strong> (Starter), <strong>₹100</strong> (Bronze), <strong>₹500</strong> (Silver), or <strong>₹1,000</strong> (Gold) coupon card.
            </Typography>
          </Box>

          {/* Round Pagination Buttons */}
          <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <IconButton
              onClick={handlePrevPage}
              disabled={isFirst || loading}
              sx={{
                border: '1px solid rgba(245, 241, 230, 0.14)',
                color: '#F5F1E6',
                backgroundColor: 'rgba(21, 58, 53, 0.4)',
                '&:hover': {
                  backgroundColor: '#E8C078',
                  color: '#0F2E2B',
                  borderColor: '#E8C078',
                },
                '&.Mui-disabled': {
                  color: 'rgba(245, 241, 230, 0.22)',
                  borderColor: 'rgba(245, 241, 230, 0.06)',
                },
                width: '44px',
                height: '44px',
              }}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                color: '#E8C078',
                fontSize: '14.5px',
                fontWeight: 600,
              }}
            >
              {page + 1} / {totalPages}
            </Typography>
            <IconButton
              onClick={handleNextPage}
              disabled={isLast || loading}
              sx={{
                border: '1px solid rgba(245, 241, 230, 0.14)',
                color: '#F5F1E6',
                backgroundColor: 'rgba(21, 58, 53, 0.4)',
                '&:hover': {
                  backgroundColor: '#E8C078',
                  color: '#0F2E2B',
                  borderColor: '#E8C078',
                },
                '&.Mui-disabled': {
                  color: 'rgba(245, 241, 230, 0.22)',
                  borderColor: 'rgba(245, 241, 230, 0.06)',
                },
                width: '44px',
                height: '44px',
              }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        </Box>

        {error && (
          <Typography sx={{ color: '#E4572E', fontSize: '14.5px', marginBottom: '20px', fontFamily: '"JetBrains Mono", monospace' }}>
            ⚠️ Error: {error}
          </Typography>
        )}

        {/* Category Filter Chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '34px' }}>
          <Chip
            label="All"
            clickable
            onClick={() => setActiveCategory("All")}
            sx={{
              padding: '9px 6px',
              fontSize: '13.5px',
              backgroundColor: activeCategory === "All" ? '#D4A24C' : 'transparent',
              color: activeCategory === "All" ? '#0F2E2B' : 'rgba(245, 241, 230, 0.66)',
              border: '1px solid rgba(245, 241, 230, 0.14)',
              fontWeight: activeCategory === "All" ? 600 : 500,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#E8C078',
                color: '#0F2E2B',
                borderColor: '#E8C078',
              },
            }}
          />
          {categories.map((c) => (
            <Chip
              key={c.id}
              label={c.name}
              clickable
              onClick={() => setActiveCategory(c.name)}
              sx={{
                padding: '9px 6px',
                fontSize: '13.5px',
                backgroundColor: activeCategory === c.name ? '#D4A24C' : 'transparent',
                color: activeCategory === c.name ? '#0F2E2B' : 'rgba(245, 241, 230, 0.66)',
                border: '1px solid rgba(245, 241, 230, 0.14)',
                fontWeight: activeCategory === c.name ? 600 : 500,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#E8C078',
                  color: '#0F2E2B',
                  borderColor: '#E8C078',
                },
              }}
            />
          ))}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress sx={{ color: '#E8C078' }} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: '24px',
            }}
          >
            {(activeCategory === "All" ? products : products.filter(p => p.categoryName === activeCategory)).map((deal) => (
              <Box
                key={deal.id}
                sx={{
                  backgroundColor: '#FAF3E6',
                  color: '#0F2E2B',
                  borderRadius: '16px',
                  display: 'flex',
                  position: 'relative',
                  boxShadow: '0 14px 30px -18px rgba(0,0,0,0.55)',
                  minHeight: '190px',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 40px -12px rgba(0,0,0,0.65)',
                  },
                }}
              >
                {/* Left Stub: Image instead of Percent */}
                <Box
                  onMouseEnter={(e) => {
                    const imgUrl = deal.imageUrl || `https://placehold.co/400x300/153A35/E8C078?text=${encodeURIComponent(deal.name || 'Deal')}`;
                    setHoveredImage({ url: imgUrl, title: deal.name, anchorEl: e.currentTarget });
                  }}
                  onMouseLeave={() => setHoveredImage(null)}
                  sx={{
                    width: '40%',
                    flexShrink: 0,
                    backgroundColor: '#0B2220',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: '#123733',
                    },
                  }}
                >
                  {/* Discount Badge (40% OFF) */}
                  <Chip
                    label={
                      deal.discount != null
                        ? `${deal.discount}% OFF`
                        : deal.marketProductValuePaise && deal.iSoretProductValuePaise
                        ? `${Math.round(((deal.marketProductValuePaise - deal.iSoretProductValuePaise) / deal.marketProductValuePaise) * 100)}% OFF`
                        : '40% OFF'
                    }
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: '8px',
                      left: '8px',
                      backgroundColor: '#E8C078',
                      color: '#0F2E2B',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 800,
                      fontSize: '10px',
                      height: '20px',
                      zIndex: 3,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                    }}
                  />
                  <Box
                    component="img"
                    src={deal.imageUrl || `https://placehold.co/300x200/153A35/E8C078?text=${encodeURIComponent(deal.name || 'Deal')}`}
                    alt={deal.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://placehold.co/300x200/153A35/E8C078?text=${encodeURIComponent(deal.name || 'Deal')}`;
                    }}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  {/* Overlay for aesthetic blending */}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(to right, rgba(21, 58, 53, 0.2), rgba(21, 58, 53, 0))',
                      pointerEvents: 'none',
                    }}
                  />
                </Box>

                {/* Divider Line with Notches */}
                <Box
                  sx={{
                    position: 'relative',
                    width: 0,
                    borderLeft: '2px dashed rgba(15, 46, 43, 0.22)',
                  }}
                >
                  {/* Top Notch */}
                  <Box
                    sx={{
                      position: 'absolute',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: '#0F2E2B',
                      top: '-8px',
                      left: '-9px',
                      zIndex: 2,
                    }}
                  />
                  {/* Bottom Notch */}
                  <Box
                    sx={{
                      position: 'absolute',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: '#0F2E2B',
                      bottom: '-8px',
                      left: '-9px',
                      zIndex: 2,
                    }}
                  />
                </Box>

                {/* Deal Body */}
                <Box
                  sx={{
                    width: '60%',
                    padding: '18px 18px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    textAlign: 'left',
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '10px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          color: '#1C6758',
                          fontWeight: 600,
                        }}
                      >
                        {deal.brand ? `${deal.categoryName} · ${deal.brand}` : deal.categoryName}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '11px',
                          fontWeight: 700,
                          backgroundColor: 'rgba(28, 103, 88, 0.12)',
                          color: '#1C6758',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {deal.tier || deal.couponCode} · ₹{getCouponPrice(deal.tier || deal.couponCode)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontSize: '16px',
                        fontWeight: 600,
                        margin: '6px 0 4px',
                        color: '#0F2E2B',
                        lineHeight: 1.25,
                      }}
                    >
                      {deal.name}
                    </Typography>
                  </Box>

                  {/* Foot */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      borderTop: '1px dashed rgba(15, 46, 43, 0.2)',
                      paddingTop: '10px',
                      marginTop: '8px',
                      gap: '8px',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '9px',
                            color: 'rgba(15, 46, 43, 0.5)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Market Price
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"Fraunces", serif',
                            fontSize: '12.5px',
                            fontWeight: 600,
                            color: 'rgba(15, 46, 43, 0.6)',
                            textDecoration: 'line-through',
                          }}
                        >
                          ₹{deal.marketProductValuePaise != null ? (deal.marketProductValuePaise / 100).toFixed(0) : (deal.marketPrice ? Number(deal.marketPrice).toFixed(0) : '0')}
                        </Typography>
                      </Box>
                      {deal.iSoretProductValuePaise != null && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '9px',
                              color: 'rgba(15, 46, 43, 0.5)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                            }}
                          >
                            iSoret Price
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: '"Fraunces", serif',
                              fontSize: '13px',
                              fontWeight: 700,
                              color: '#1C6758',
                            }}
                          >
                            ₹{(deal.iSoretProductValuePaise / 100).toFixed(0)}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Buy Button */}
                    <Button
                      variant="contained"
                      size="small"
                      onClick={async (e) => {
                        e.stopPropagation();
                        const coupon = getEquivalentCoupon(deal);
                        await addToCart(coupon);
                        setToastMessage(`Added ${coupon.tier} Digital Card to cart!`);
                        setToastOpen(true);
                        setIsCartOpen(true);
                      }}
                      sx={{
                        backgroundColor: '#0F2E2B',
                        color: '#E8C078',
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        padding: '4px 14px',
                        borderRadius: '6px',
                        minWidth: 'auto',
                        whiteSpace: 'nowrap',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#153A35',
                          color: '#FFF',
                          boxShadow: '0 4px 12px rgba(15, 46, 43, 0.25)',
                        },
                      }}
                    >
                      Buy
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Container>

      {/* Snackbar/Toast Notifications */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={2200}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="success"
          sx={{
            width: '100%',
            backgroundColor: '#1C6758',
            color: '#F5F1E6',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '13px',
            '& .MuiAlert-icon': {
              color: '#E8C078',
            },
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>

      {/* Full Size Image Mouse Hover Popover */}
      <Popover
        open={Boolean(hoveredImage?.anchorEl)}
        anchorEl={hoveredImage?.anchorEl}
        onClose={() => setHoveredImage(null)}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              pointerEvents: 'none',
              background: '#0F2E2B',
              border: '2px solid #E8C078',
              borderRadius: '16px',
              padding: '12px',
              boxShadow: '0 24px 48px rgba(0,0,0,0.85)',
              maxWidth: '360px',
              zIndex: 1400,
            },
          },
        }}
        sx={{
          pointerEvents: 'none',
        }}
      >
        {hoveredImage && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              component="img"
              src={hoveredImage.url}
              alt={hoveredImage.title || 'Product Image'}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://placehold.co/400x300/153A35/E8C078?text=${encodeURIComponent(hoveredImage.title || 'Deal')}`;
              }}
              sx={{
                maxWidth: '320px',
                maxHeight: '320px',
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '10px',
                backgroundColor: '#153A35',
              }}
            />
            {hoveredImage.title && (
              <Typography
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#F5F1E6',
                  mt: 1.5,
                  textAlign: 'center',
                }}
              >
                {hoveredImage.title}
              </Typography>
            )}
          </Box>
        )}
      </Popover>
    </Box>
  );
}
